// ManageNews.js
import React, { useState, useEffect } from 'react';
import { 
  getAllNews, 
  createNews, 
  updateNews, 
  deleteNews 
} from '../../api/news';
import Button from '../../components/ui/Button';
import './ManageEvents.css';

const ManageNews = () => {
  const [newsPosts, setNewsPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general',
    author: '',
    imageUrl: '',
    active: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const newsData = await getAllNews();
      setNewsPosts(newsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (!formData.title.trim() || !formData.content.trim()) {
        setError('Please fill in all required fields');
        setSubmitting(false);
        return;
      }

      if (editingNews) {
        await updateNews(editingNews.id, formData);
        setSuccess('News post updated successfully!');
      } else {
        await createNews(formData);
        setSuccess('News post created successfully!');
      }

      resetForm();
      await fetchNews();
      setShowForm(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error submitting news:', err);
      setError(err.message || 'Failed to save news post. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (newsPost) => {
    setEditingNews(newsPost);
    setFormData({
      title: newsPost.title || '',
      content: newsPost.content || '',
      category: newsPost.category || 'general',
      author: newsPost.author || '',
      imageUrl: newsPost.imageUrl || '',
      active: newsPost.active !== undefined ? newsPost.active : true,
    });
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (newsId) => {
    if (!window.confirm('Are you sure you want to delete this news post?')) {
      return;
    }

    try {
      await deleteNews(newsId);
      setSuccess('News post deleted successfully!');
      await fetchNews();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting news:', err);
      setError('Failed to delete news post. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: 'general',
      author: '',
      imageUrl: '',
      active: true,
    });
    setEditingNews(null);
    setError(null);
  };

  const handleCancelForm = () => {
    resetForm();
    setShowForm(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const getCategoryBadge = (category) => {
    const badges = {
      updates: { class: 'category-updates', label: 'Updates' },
      events: { class: 'category-events', label: 'Events' },
      community: { class: 'category-community', label: 'Community' },
      general: { class: 'category-general', label: 'General' }
    };
    return badges[category] || badges.general;
  };

  return (
    <div className="manage-events manage-news">
      <div className="manage-events-header">
        <h1>Manage News</h1>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} variant="primary">
            + Create New News Post
          </Button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="event-form-container">
          <div className="event-form-header">
            <h2>{editingNews ? 'Edit News Post' : 'Create New News Post'}</h2>
            <button className="close-btn" onClick={handleCancelForm}>×</button>
          </div>

          <form onSubmit={handleSubmit} className="event-form">
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter news title"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="content">Content *</label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Enter news content (full article text)"
                rows="10"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="general">General</option>
                  <option value="updates">Updates</option>
                  <option value="events">Events</option>
                  <option value="community">Community</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="author">Author</label>
                <input
                  type="text"
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="Author name"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="imageUrl">Image URL (optional)</label>
              <input
                type="url"
                id="imageUrl"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                />
                <span>Active (visible to users)</span>
              </label>
            </div>

            <div className="form-actions">
              <Button type="button" onClick={handleCancelForm} variant="secondary">
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Saving...' : (editingNews ? 'Update News Post' : 'Create News Post')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading news posts...</div>
      ) : (
        <div className="events-table-container">
          <table className="events-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Content Preview</th>
                <th>Category</th>
                <th>Author</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {newsPosts.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">No news posts found. Create your first news post!</td>
                </tr>
              ) : (
                newsPosts.map((newsPost) => {
                  const categoryBadge = getCategoryBadge(newsPost.category);
                  return (
                    <tr key={newsPost.id}>
                      <td>
                        <strong>{newsPost.title}</strong>
                      </td>
                      <td>
                        <div className="content-preview">
                          {newsPost.content?.substring(0, 100)}
                          {newsPost.content?.length > 100 ? '...' : ''}
                        </div>
                      </td>
                      <td>
                        <span className={`priority-badge ${categoryBadge.class}`}>
                          {categoryBadge.label}
                        </span>
                      </td>
                      <td>{newsPost.author || 'N/A'}</td>
                      <td>
                        <span className={`status-badge ${newsPost.active ? 'status-active' : 'status-inactive'}`}>
                          {newsPost.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{formatDate(newsPost.createdAt)}</td>
                      <td>
                        <div className="action-buttons">
                          <Button 
                            onClick={() => handleEdit(newsPost)} 
                            variant="secondary"
                            size="small"
                          >
                            Edit
                          </Button>
                          <Button 
                            onClick={() => handleDelete(newsPost.id)} 
                            variant="danger"
                            size="small"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManageNews;
